// AskDemo: an owner asks about their exact unit on their phone; a grounded answer streams in
// with the manual page it came from, the unit's context, one next step and a quiet way to a person.
// Loops gently while on screen; reduced motion shows the final frame. Optional rotation through units.
import { useEffect, useMemo, useRef, useState } from 'react';
import { cx } from '../ui';
import { heroUnits, type HeroUnit } from '../heroUnits';
import { defaultUnit, qaFor, type Tone } from './data';
import { IconDoc, IconSend, useAiStyles, useOnScreen, useStream, useTimeline } from './shared';
import { Device } from '../phone';
import { sectorByKey } from '../variants/c/sectors';

export type AskDemoProps = {
  tone?: Tone;
  unit?: HeroUnit;
  /** Move to the next hero unit after each full play (ignored when `unit` is given). */
  rotate?: boolean;
  width?: number;
  className?: string;
  /** Called when the unit on screen changes (for syncing a photo or caption). */
  onUnitChange?: (u: HeroUnit) => void;
};

export function AskDemo({ tone = 'light', unit, rotate = false, width = 300, className, onUnitChange }: AskDemoProps) {
  useAiStyles();
  const [idx, setIdx] = useState(0);
  const u = unit ?? (rotate ? heroUnits[idx] : defaultUnit);
  const qa = qaFor(u);
  const [ref, onScreen] = useOnScreen<HTMLDivElement>(0.2);

  const durations = useMemo(
    () => [700, qa.q.split(' ').length * 60 + 350, 1250, qa.a.split(' ').length * 55 + 450, 650, 4600],
    [qa.q, qa.a]
  );
  const [step] = useTimeline(durations, onScreen, {
    onLoop: () => {
      if (unit || !rotate) return;
      const next = (idx + 1) % heroUnits.length;
      setIdx(next);
      onUnitChange?.(heroUnits[next]);
    },
  });
  const typing = useStream(qa.q, step === 1, step > 1, 60);
  const answer = useStream(qa.a, step === 3, step > 3, 55);
  // keep the newest message in view, like a real chat
  const chatRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [step, answer.shown, u.key]);

  const sec = sectorByKey(u.key);
  const short = u.model.split(' ').slice(1).join(' ');
  return (
    <div ref={ref} className={cx('ai-ask', className)} data-tone={tone}>
      <Device s={sec} zoom={width / 252} label={`An owner asks about their ${u.model}, serial ${u.serial}: "${qa.q}" The answer cites ${qa.cite} and offers: ${qa.action}, or talk to a person.`}>
        <div className="op-page" aria-hidden="true" key={u.key}>
          <div className="op-nav">
            <span className="op-nav-sp" />
            <span>Ask your {short}</span>
            <span className="op-nav-sp" />
          </div>
          <div className="op-ctx">
            <span className="op-ctx-th">
              <img src={u.product} alt="" width={44} height={44} />
            </span>
            <span className="op-ctx-t">
              <b>{u.model}</b>
              <span className="op-num">{u.serial}</span>
            </span>
            <span className="op-pill">In warranty</span>
          </div>
          <div className="ai-op-chat" ref={chatRef}>
            {step < 2 && (
              <div className="op-a">
                <span className="op-a-who">
                  <span className="op-brand-mark is-sm">{sec.mark}</span>
                  {u.brand} assistant
                </span>
                <p>I know your {short}: its serial, warranty and history. Ask me anything about it.</p>
              </div>
            )}
            {step >= 2 && <div className="op-q ai-cut">{qa.q}</div>}
            {step === 2 && (
              <div className="op-day ai-cut">
                Reading the {qa.cite.replace(/ p\.\d+$/, '').toLowerCase()} for your {short}
              </div>
            )}
            {step >= 3 && (
              <div className="op-a">
                <span className="op-a-who">
                  <span className="op-brand-mark is-sm">{sec.mark}</span>
                  {u.brand} assistant
                </span>
                <p>
                  {answer.shown}
                  {!answer.complete && <span className="ai-caret" />}
                </p>
                {step >= 4 && (
                  <span className="op-src ai-cut">
                    <IconDoc size={10} />
                    {qa.cite}
                  </span>
                )}
              </div>
            )}
            {step >= 5 && (
              <div className="op-sugs ai-cut">
                <span>{qa.action}</span>
                <span>Talk to a person</span>
              </div>
            )}
          </div>
          <div className="op-input">
            <span>{step === 1 ? typing.shown : `Ask about your ${short}`}</span>
            <span className="op-ask-go">
              <IconSend size={11} />
            </span>
          </div>
        </div>
      </Device>
    </div>
  );
}

export default AskDemo;
